import React from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { cn } from "../../lib/utils";
import { CreditCard, FileText, HelpCircle, Link2, Monitor, ShieldCheck, UserCircle, UserPlus } from "lucide-react";

interface SettingsNavProps {
  tenantSlug: string;
  campaignKey?: string | null;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  preload?: () => Promise<unknown>;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

export const SettingsNav: React.FC<SettingsNavProps> = ({ tenantSlug, campaignKey }) => {
  const [searchParams] = useSearchParams();
  const activeCampaignKey = campaignKey || searchParams.get("campaign");
  const settingsBase = tenantSlug ? `/t/${tenantSlug}/settings` : "/settings";
  const sections: NavSection[] = React.useMemo(
    () => [
      {
        title: "Workspace",
        items: [
          {
            to: `${settingsBase}/account`,
            label: "Account",
            icon: <UserCircle className="h-4 w-4" />,
            preload: () => import("../../pages/AccountManagement"),
          },
          {
            to: `${settingsBase}/invite-users`,
            label: "Invite Users",
            icon: <UserPlus className="h-4 w-4" />,
            preload: () => import("../../pages/InviteUsers"),
          },
        ],
      },
      {
        title: "Preferences",
        items: [
          {
            to: `${settingsBase}/display`,
            label: "Display",
            icon: <Monitor className="h-4 w-4" />,
            preload: () => import("../../pages/DisplaySettings"),
          },
        ],
      },
      {
        title: "Support",
        items: [
          {
            to: `${settingsBase}/support`,
            label: "Support",
            icon: <HelpCircle className="h-4 w-4" />,
            preload: () => import("../../pages/Support"),
          },
          {
            to: activeCampaignKey
              ? `${settingsBase}/advertiser-verification/${activeCampaignKey}`
              : `${settingsBase}/advertiser-verification`,
            label: "Advertiser Verification",
            icon: <ShieldCheck className="h-4 w-4" />,
            disabled: !activeCampaignKey,
            preload: () => import("../../pages/AdvertiserVerification"),
          },
        ],
      },
      {
        title: "Integrations",
        items: [
          {
            to: `${settingsBase}/integrations`,
            label: "Ad Platforms",
            icon: <Link2 className="h-4 w-4" />,
            preload: () => import("../../pages/SettingsIntegrations"),
          },
        ],
      },
      {
        title: "Billing",
        items: [
          {
            to: `${settingsBase}/plan`,
            label: "Plan",
            icon: <CreditCard className="h-4 w-4" />,
            preload: () => import("../../pages/SettingsPlan"),
          },
          {
            to: `${settingsBase}/invoices`,
            label: "Payment & invoices",
            icon: <FileText className="h-4 w-4" />,
            preload: () => import("../../pages/SettingsInvoices"),
          },
        ],
      },
    ],
    [activeCampaignKey, settingsBase],
  );

  return (
    <nav className="w-full sm:w-56 shrink-0 space-y-6">
      {sections.map((section) => (
        <div key={section.title}>
          <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {section.title}
          </h3>
          <ul className="space-y-1">
            {section.items.map((item) => (
              <li key={item.label}>
                {item.disabled ? (
                  <span className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground/50 cursor-not-allowed">
                    {item.icon}
                    {item.label}
                  </span>
                ) : (
                  <NavLink
                    to={item.to}
                    end
                    onMouseEnter={item.preload}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )
                    }
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export default SettingsNav;
